import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, FormGroup,
  Paper, Radio, RadioGroup, TextField, Typography, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { lmsService } from '../../services/api';

export default function LmsTakeQuizPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await lmsService.quiz(id);
      setQuiz(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setAnswer = (qi, value) => {
    setAnswers((prev) => ({ ...prev, [qi]: value }));
  };

  const toggleMulti = (qi, optIdx) => {
    setAnswers((prev) => {
      const cur = Array.isArray(prev[qi]) ? [...prev[qi]] : [];
      const pos = cur.indexOf(optIdx);
      if (pos >= 0) cur.splice(pos, 1);
      else cur.push(optIdx);
      return { ...prev, [qi]: cur };
    });
  };

  const handleSubmit = async () => {
    if (submitting || !quiz) return;
    const payload = (quiz.questions || []).map((_, qi) => ({
      qi,
      answer: answers[qi] ?? null,
    }));
    setSubmitting(true);
    try {
      const res = await lmsService.quizSubmit(id, { answers: payload });
      setResult(res.data);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }
  if (error || !quiz) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lms/tests')}>До тестів</Button>
        <Typography color="error" sx={{ mt: 2 }}>{error || 'Не знайдено'}</Typography>
      </Box>
    );
  }

  if (result) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lms/tests')} sx={{ mb: 2 }}>
          До тестів
        </Button>
        <Alert severity={result.passed === false ? 'warning' : 'success'} sx={{ mb: 2 }}>
          Результат: {result.score}/{result.total} ({result.pct}%)
          {result.passed == null ? '' : result.passed ? ' — пройдено' : ' — не пройдено'}
        </Alert>
        {(result.breakdown || []).map((b) => (
          <Paper key={b.qi} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
            <Typography variant="body2">
              Питання {b.qi + 1}: {b.correct === true ? '✓ правильно' : b.correct === false ? '✗ неправильно' : '—'}
            </Typography>
            {b.explanation ? (
              <Typography variant="caption" color="text.secondary">{b.explanation}</Typography>
            ) : null}
          </Paper>
        ))}
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/lms/results')}>
          До результатів
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lms/tests')} sx={{ mb: 2 }}>
        До тестів
      </Button>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{quiz.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {quiz.settings?.pass_mark != null ? `Поріг: ${quiz.settings.pass_mark}%` : 'Поріг не задано'}
        {' · '}
        Спроба {(quiz.my_attempts || 0) + 1}
        {quiz.settings?.max_attempts
          ? ` з ${quiz.settings.max_attempts}`
          : (quiz.allow_retake ? ' (перездача дозволена)' : '')}
      </Typography>
      {(quiz.questions || []).map((q, qi) => (
        <Paper key={qi} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>{qi + 1}. {q.question}</Typography>
          {q.type === 'open' ? (
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={answers[qi] || ''}
              onChange={(e) => setAnswer(qi, e.target.value)}
              label="Ваша відповідь"
            />
          ) : q.type === 'multi' ? (
            <FormGroup>
              {(q.options || []).map((opt, oi) => (
                <FormControlLabel
                  key={oi}
                  control={
                    <Checkbox
                      checked={Array.isArray(answers[qi]) && answers[qi].includes(oi)}
                      onChange={() => toggleMulti(qi, oi)}
                    />
                  }
                  label={opt}
                />
              ))}
            </FormGroup>
          ) : (
            <RadioGroup
              value={answers[qi] ?? ''}
              onChange={(e) => setAnswer(qi, Number(e.target.value))}
            >
              {(q.options || []).map((opt, oi) => (
                <FormControlLabel key={oi} value={oi} control={<Radio />} label={opt} />
              ))}
            </RadioGroup>
          )}
        </Paper>
      ))}
      <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
        {submitting ? <CircularProgress size={20} /> : 'Здати тест'}
      </Button>
    </Box>
  );
}
